import React, { useEffect, useState } from "react";
import { Card, Tag, Button, Spin, Collapse, Tooltip, Empty, Badge, Segmented } from "antd";
import { ClockCircleOutlined, UserOutlined, PhoneOutlined, ArrowRightOutlined, PlusOutlined, CheckCircleOutlined, TeamOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import apiClient from "@/utils/apiClient";
import AddFollowUpModal from "../AddFollowUpModal";

const { Panel } = Collapse;

const PendingFollowupsAdminCard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("my");
  const [myFollowups, setMyFollowups] = useState([]);
  const [adminData, setAdminData] = useState({ totalPendingCount: 0, users: [] });
  const [selectedLead, setSelectedLead] = useState(null);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);

  const fetchPendingFollowups = async () => {
    try {
      setLoading(true);
      const [myRes, adminRes] = await Promise.allSettled([
        apiClient.get("/leads/pending-followups/my"),
        apiClient.get("/leads/pending-followups/admin")
      ]);

      if (myRes.status === "fulfilled" && myRes.value.data?.success) {
        setMyFollowups(myRes.value.data.data || []);
      }

      if (adminRes.status === "fulfilled" && adminRes.value.data?.success) {
        setAdminData(adminRes.value.data.data || { totalPendingCount: 0, users: [] });
      }
    } catch (err) {
      console.error("Failed to load pending followups:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingFollowups();
  }, []);

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "Not Scheduled";
    const d = new Date(dateStr);
    return d.toLocaleString("en-IN", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getOverdueLabel = (dateStr) => {
    if (!dateStr) return "";
    const diffMs = new Date() - new Date(dateStr);
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours < 1) return "Due just now";
    if (diffHours < 24) return `${diffHours}h overdue`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d overdue`;
  };

  return (
    <>
      <Card
        style={{
          borderRadius: 16,
          boxShadow: "0 4px 16px rgba(15, 23, 42, 0.05)",
          border: "1px solid #fee2e2",
          marginBottom: 24,
        }}
        title={
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 12,
                  background: "#fef2f2",
                  color: "#dc2626",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: 18,
                }}
              >
                <ClockCircleOutlined />
              </div>
              <div>
                <span style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>
                  Daily Pending Follow-Ups
                </span>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  Actionable daily follow-up queue for personal queries & team tracking
                </div>
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Segmented
                value={activeTab}
                onChange={setActiveTab}
                options={[
                  {
                    label: (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 600 }}>
                        <UserOutlined /> My Pending ({myFollowups.length})
                      </span>
                    ),
                    value: "my",
                  },
                  {
                    label: (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 600 }}>
                        <TeamOutlined /> All Team ({adminData.totalPendingCount || 0})
                      </span>
                    ),
                    value: "team",
                  },
                ]}
              />
            </div>
          </div>
        }
      >
        {loading ? (
          <div style={{ padding: 24, textAlign: "center" }}>
            <Spin tip="Loading pending follow-ups..." />
          </div>
        ) : activeTab === "my" ? (
          /* =====================================================
              MY PERSONAL DAILY PENDING FOLLOW-UPS
          ===================================================== */
          myFollowups.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span style={{ color: "#16a34a", fontWeight: 600, fontSize: 14 }}>
                  🎉 Awesome! You have zero pending follow-ups for today.
                </span>
              }
            />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {myFollowups.map((lead) => {
                const followUpDate = lead.nextFollowUp || lead.lastFollowUp?.nextFollowUp;
                return (
                  <div
                    key={lead._id}
                    style={{
                      padding: "14px 18px",
                      background: "#fefcbf10",
                      border: "1px solid #fee2e2",
                      borderRadius: 12,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 12,
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 240 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        <span style={{ fontWeight: 700, color: "#0f172a", fontSize: 15 }}>
                          {lead.customerName}
                        </span>
                        <Tag style={{ fontSize: 11, background: "#f1f5f9", borderRadius: 8, fontWeight: 600 }}>
                          #{lead.leadId}
                        </Tag>
                        <Tag color="red" style={{ fontSize: 11, borderRadius: 8, fontWeight: 700 }}>
                          {getOverdueLabel(followUpDate)}
                        </Tag>
                      </div>

                      <div style={{ fontSize: 13, color: "#334155", display: "flex", gap: 18, flexWrap: "wrap" }}>
                        <span>
                          <PhoneOutlined style={{ marginRight: 4, color: "#2563eb" }} />
                          <a href={`tel:${lead.phone}`} style={{ color: "inherit" }}>
                            {lead.phone || "No phone"}
                          </a>
                        </span>
                        <span>
                          <strong>Dest:</strong> {lead.destinationSnapshot || lead.destination?.name || "-"}
                        </span>
                        <span>
                          <ClockCircleOutlined style={{ marginRight: 4, color: "#dc2626" }} />
                          {formatDateTime(followUpDate)}
                        </span>
                      </div>

                      {(lead.lastFollowUp?.notes || lead.specialRequests) && (
                        <div style={{ fontSize: 12, color: "#64748b", marginTop: 6, fontStyle: "italic", background: "#f8fafc", padding: "4px 10px", borderRadius: 6, borderLeft: "3px solid #cbd5e1" }}>
                          "{lead.lastFollowUp?.notes || lead.specialRequests}"
                        </div>
                      )}
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Button
                        size="small"
                        type="primary"
                        icon={<PlusOutlined />}
                        style={{ borderRadius: 8, fontWeight: 600 }}
                        onClick={() => {
                          setSelectedLead(lead);
                          setShowFollowUpModal(true);
                        }}
                      >
                        Log Follow-Up
                      </Button>

                      <Button
                        size="small"
                        icon={<ArrowRightOutlined />}
                        style={{ borderRadius: 8 }}
                        onClick={() => navigate(`/trips/${lead._id}`)}
                      >
                        View Lead
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          /* =====================================================
              ALL TEAM PENDING FOLLOW-UPS (ADMIN OVERVIEW)
          ===================================================== */
          adminData.users.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span style={{ color: "#16a34a", fontWeight: 600 }}>
                  🎉 No pending follow-ups! All team members are caught up.
                </span>
              }
            />
          ) : (
            <Collapse
              ghost
              expandIconPosition="right"
              defaultActiveKey={[adminData.users[0]?.userId]}
            >
              {adminData.users.map((u) => (
                <Panel
                  key={u.userId}
                  header={
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", paddingRight: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 999,
                            background: "#e0f2fe",
                            color: "#0284c7",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 700,
                            fontSize: 13,
                          }}
                        >
                          <UserOutlined />
                        </div>
                        <div>
                          <span style={{ fontWeight: 600, fontSize: 14, color: "#1f2937" }}>
                            {u.userName}
                          </span>
                          <Tag style={{ marginLeft: 8, fontSize: 11, borderRadius: 10, border: "none", background: "#f3f4f6" }}>
                            {u.userRole}
                          </Tag>
                        </div>
                      </div>

                      <Tag color="error" style={{ borderRadius: 12, fontWeight: 700, padding: "2px 10px" }}>
                        {u.pendingCount} Pending
                      </Tag>
                    </div>
                  }
                >
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {u.leads.map((lead) => (
                      <div
                        key={lead._id}
                        style={{
                          padding: "12px 16px",
                          background: "#fff",
                          border: "1px solid #f3f4f6",
                          borderRadius: 12,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                          gap: 12,
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 220 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                            <span style={{ fontWeight: 700, color: "#111827", fontSize: 14 }}>
                              {lead.customerName}
                            </span>
                            <Tag style={{ fontSize: 11, background: "#f3f4f6", borderRadius: 8 }}>
                              {lead.leadId}
                            </Tag>
                            <Tag color="red" style={{ fontSize: 11, borderRadius: 8 }}>
                              {getOverdueLabel(lead.nextFollowUp)}
                            </Tag>
                          </div>

                          <div style={{ fontSize: 12, color: "#4b5563", display: "flex", gap: 16, flexWrap: "wrap" }}>
                            <span><PhoneOutlined style={{ marginRight: 4 }} />{lead.phone || "N/A"}</span>
                            <span><strong>Dest:</strong> {lead.destination}</span>
                            <span><ClockCircleOutlined style={{ marginRight: 4 }} />{formatDateTime(lead.nextFollowUp)}</span>
                          </div>

                          {lead.notes && (
                            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4, fontStyle: "italic" }}>
                              "{lead.notes}"
                            </div>
                          )}
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <Button
                            size="small"
                            type="primary"
                            ghost
                            icon={<PlusOutlined />}
                            onClick={() => {
                              setSelectedLead(lead);
                              setShowFollowUpModal(true);
                            }}
                          >
                            Log Follow-Up
                          </Button>

                          <Button
                            size="small"
                            icon={<ArrowRightOutlined />}
                            onClick={() => navigate(`/trips/${lead._id}`)}
                          >
                            View Lead
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </Panel>
              ))}
            </Collapse>
          )
        )}
      </Card>

      {selectedLead && (
        <AddFollowUpModal
          visible={showFollowUpModal}
          onClose={() => setShowFollowUpModal(false)}
          leadId={selectedLead._id}
          customerName={selectedLead.customerName}
          onSuccess={() => {
            fetchPendingFollowups();
          }}
        />
      )}
    </>
  );
};

export default PendingFollowupsAdminCard;
